// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import CellExpressionDialog from "./components/cell_expression_dialog";

import ExcelJS from "exceljs";
import ExcelSheetPreviewer from "./components/excel_preview";

function CreateExcelReportTemplatePage() {
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [workBook, setWorkBook] = useState(null);

  const [formFields, setFormFields] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [mappings, setMappings] = useState({});
  const [loadedWorkBook, setLoadedWorkBook] = useState(null);

  const handleSave = (sheetIndex, cell, expr) => {
    setMappings((prev) => ({
      ...prev,
      [sheetIndex]: {
        ...(prev[sheetIndex] || {}),
        [cell]: expr,
      },
    }));
  };

  const handleDeleteMapping = (sheetIndex, cell) => {
    setMappings((prev) => {
      if (!prev[sheetIndex]) return prev;
      const newSheetMap = {
        ...prev[sheetIndex],
      };

      delete newSheetMap[cell];
      const updated = {
        ...prev,
        [sheetIndex]: newSheetMap,
      };
      if (Object.keys(newSheetMap).length === 0) {
        delete updated[sheetIndex];
      }

      return updated;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/excel/templates/init.json`)
        .then((result) => {
          setShowLoading(false);
          // console.log(result.data.data);
          if (result.data.status === "success") {
            setCampaigns(result.data.data.campaigns);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  async function paresExcel(excelFile = null) {
    if (selectedCampaign === null) {
      setSeverity("error");
      setErrMsg("Please select a campaign.");
      setOpen(true);
      return;
    }
    setShowLoading(true);
    const formData = new FormData();
    formData.append("reference", selectedCampaign);
    formData.append("file", excelFile);
    await axios
      .post("portal/excel/templates/parse.json", formData)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          console.log(result.data.data.wb);
          setWorkBook(result.data.data.wb);
          setFormFields(result.data.data.form_fields);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  function uploadExcel() {
    if (selectedCampaign === null) {
      setSeverity("error");
      setErrMsg("Please select a campaign.");
      setOpen(true);
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) {
        setSeverity("error");
        setErrMsg("No file found.");
        setOpen(true);
        return;
      }

      await paresExcel(file);

      // Excel Preview
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(await file.arrayBuffer());
      // console.log(wb);
      setLoadedWorkBook(wb);
    };

    input.click();
  }

  const [activeSheetIdx, setActiveSheetIdx] = useState(0);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <CellExpressionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        cell={selectedCell}
        fields={formFields}
        sheetIndex={selectedSheet}
        onSave={handleSave}
      />
      <MDBox mb={5}>
        <MDBox>
          <MDBox mt={1} mb={2} p={1}>
            <MDTypography variant="h6">Excel Report Builder</MDTypography>
          </MDBox>
          <Card sx={{ height: "100%", marginTop: "0px" }}>
            <MDBox p={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox>
                    <FormControl sx={{ minWidth: 120, width: "100%" }}>
                      <InputLabel>Select Campaign</InputLabel>
                      <Select
                        label="Select Campaign"
                        onChange={(e) => {
                          setSelectedCampaign(e.target.value);
                        }}
                        sx={{ padding: "12px", paddingLeft: "3px" }}
                      >
                        {
                          // --> Notice below changes
                          campaigns.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                            campaigns.map((record) => (
                              <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                            ))
                        }
                      </Select>
                    </FormControl>
                    <MDBox mt={3} align="left">
                      <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() => {
                          uploadExcel();
                        }}
                        startIcon={<Icon>upload</Icon>}
                      >
                        Upload Excel Template
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox align="right">
                    <MDButton
                      variant="contained"
                      color="dark"
                      onClick={() => {
                        uploadExcel();
                      }}
                      startIcon={<Icon>check</Icon>}
                    >
                      Save Excel Template
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>
        {loadedWorkBook && (
          <MDBox mt={5}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h6" align="center">Loaded WorkBook</MDTypography>
                <MDBox mt={1}>
                  <Tabs
                    value={activeSheetIdx}
                    onChange={(e, newIdx) => {
                      setActiveSheetIdx(newIdx);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {loadedWorkBook.worksheets.map((sheet, idx) => (
                      <Tab key={idx} label={<MDTypography variant="h6" fontWeight="regular" fontSize={14} align="center">{sheet.name}</MDTypography>} />
                    ))}
                  </Tabs>
                </MDBox>
                <MDBox mt={3} mb={5}>
                  <ExcelSheetPreviewer
                    worksheet={loadedWorkBook.worksheets[activeSheetIdx]}
                    mappedCells={mappings?.[activeSheetIdx]}
                    onCellClick={(cell) => {
                      setSelectedCell(cell);
                      setSelectedSheet(activeSheetIdx);
                      setDialogOpen(true);
                    }}
                  />
                </MDBox>
                <MDBox mb={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={6} md={6} lg={6}>
                      <MDBox>
                        {/* <Grid container spacing={3}>
                          <Grid item xs={6} md={6} lg={6}>
                            <MDTypography variant="h6">Cell</MDTypography>
                          </Grid>
                          <Grid item xs={6} md={6} lg={6}>
                            <MDTypography variant="h6" align="right">
                              Expression
                            </MDTypography>
                          </Grid>
                        </Grid> */}
                        <MDTypography variant="h6" align="center">
                          Mappings
                        </MDTypography>
                        <Divider />
                        <Grid container spacing={3}>
                          {Object.entries(mappings?.[activeSheetIdx] || {}).map(
                            ([cellAddr, expr]) => (
                              <>
                                <Grid item xs={6} md={3} lg={3}>
                                  <MDTypography variant="h6" fontWeight="light" pt={2}>
                                    {cellAddr}
                                  </MDTypography>
                                </Grid>

                                <Grid item xs={6} md={9} lg={9}>
                                  <MDBox align="right">
                                    <MDInput
                                      disabled
                                      margin="dense"
                                      multiline
                                      fullWidth
                                      variant="outlined"
                                      value={expr ?? ""}
                                    />

                                    {expr && (
                                      <>
                                        <MDButton
                                          size="small"
                                          onClick={() =>
                                            handleDeleteMapping(activeSheetIdx, cellAddr)
                                          }
                                          startIcon={<Icon>clear</Icon>}
                                        >
                                          Clear
                                        </MDButton>
                                        &nbsp;
                                      </>
                                    )}

                                    <MDButton
                                      variant="contained"
                                      size="small"
                                      color="dark"
                                      onClick={() => {
                                        setSelectedSheet(activeSheetIdx);
                                        setSelectedCell(cellAddr);
                                        setDialogOpen(true);
                                      }}
                                      startIcon={<Icon>settings_ethernet</Icon>}
                                    >
                                      Map {cellAddr}
                                    </MDButton>
                                  </MDBox>
                                </Grid>
                              </>
                            )
                          )}
                        </Grid>
                      </MDBox>
                    </Grid>
                    <Grid item xs={6} md={6} lg={6}>
                      <MDTypography variant="h6" align="center">
                        Scopes
                      </MDTypography>
                      <Divider />
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}

        {workBook && 1 === 2 && (
          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8} lg={8}>
                {workBook.map((sheet, idx) => (
                  <MDBox mt={3}>
                    <Card>
                      <MDBox p={2}>
                        <MDBox>
                          <MDTypography variant="h6" fontSize={16}>
                            {sheet.name}
                          </MDTypography>
                          <MDTypography variant="subtitle2" fontSize={14}>
                            Enter expressions or values for each required cell in your excel
                            template. Ignore unneeded cells.
                          </MDTypography>
                        </MDBox>
                        <MDBox mt={3}>
                          <Grid container spacing={3}>
                            <Grid item xs={6} md={6} lg={6}>
                              <MDTypography variant="h6">Cell</MDTypography>
                            </Grid>
                            <Grid item xs={6} md={6} lg={6}>
                              <MDTypography variant="h6" align="right">
                                Expression
                              </MDTypography>
                            </Grid>
                            <Divider />
                            {sheet.cells.map((cell) => (
                              <>
                                <Grid item xs={6} md={3} lg={3}>
                                  <MDTypography variant="h6" fontWeight="light" pt={2}>
                                    {cell.cell}
                                  </MDTypography>
                                </Grid>
                                <Grid item xs={6} md={9} lg={9}>
                                  <MDBox align="right">
                                    <MDInput
                                      disabled
                                      name="title"
                                      margin="dense"
                                      label="Expression"
                                      type="text"
                                      multiline
                                      fullWidth
                                      variant="outlined"
                                      value={mappings?.[idx]?.[cell.cell] || ""}
                                    />
                                    {mappings?.[idx]?.[cell.cell] && (
                                      <>
                                        <MDButton
                                          variant="contained"
                                          size="small"
                                          onClick={() => {
                                            handleDeleteMapping(idx, cell.cell);
                                          }}
                                          startIcon={<Icon>clear</Icon>}
                                        >
                                          Clear
                                        </MDButton>
                                        &nbsp;
                                      </>
                                    )}
                                    <MDButton
                                      variant="contained"
                                      size="small"
                                      color="dark"
                                      onClick={() => {
                                        setSelectedSheet(idx);
                                        setSelectedCell(cell.cell);
                                        setDialogOpen(true);
                                      }}
                                      startIcon={<Icon>settings_ethernet</Icon>}
                                    >
                                      Map {cell.cell}
                                    </MDButton>
                                  </MDBox>
                                </Grid>
                              </>
                            ))}
                          </Grid>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </MDBox>
                ))}
              </Grid>
              <Grid item xs={12} md={4} lg={4}>
                <MDBox mt={3}>
                  <Card>
                    <MDBox p={2}>
                      <MDBox mb={3}>
                        <MDTypography variant="h6">Form Fields</MDTypography>
                        <Divider />
                      </MDBox>
                      <MDBox>
                        {formFields.map((fld) => (
                          <MDBox mb={1}>
                            <ListItemText
                              primary={<MDTypography fontSize={14}>{fld.name}</MDTypography>}
                              secondary={
                                <MDTypography
                                  fontSize={12}
                                  fontWeight="light"
                                >{`${fld.key}`}</MDTypography>
                              }
                            />
                            <Divider />
                          </MDBox>
                        ))}
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CreateExcelReportTemplatePage;
